import type { Filter } from '@/models/Filter';
import { useCallback, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import type { BrandAndChainResponse } from '@/app/api/venue-autocomplete/route';

const MultiSelect = dynamic(() => import('@/components/shared/MultiSelect'), { ssr: false });

type Props = {
  options: BrandAndChainResponse['countryOptions'];
  selected: Filter;
  setSelected: (selections: Filter) => void;
  testID?: string;
};

export default function CountryFilter({ options, selected, setSelected, testID }: Props) {
  const { clear, noMatchFound } = options.labels;
  const [disableClear, setDisableClear] = useState<boolean>(true);

  const multiSelectOptions = useMemo(
    () =>
      options.options.map(v => {
        // Filter out already selected values from selected prop
        const selectedValues = v.values.filter(option => selected?.COUNTRY_CODE?.includes(option.value));
        const unselectedValues = v.values.filter(option => !selected?.COUNTRY_CODE?.includes(option.value));

        // Apply logic separately to each array
        const selectedValuesSorted = selectedValues
          .map(option => ({
            label: <div className="-mt-px break-all text-b-md/tight">{option.label}</div>,
            value: option.value
          }))
          .sort((a, b) => a.label.props.children.localeCompare(b.label.props.children));

        const unselectedValuesSorted = unselectedValues
          .map(option => ({
            label: <div className="-mt-px break-all text-b-md/tight">{option.label}</div>,
            value: option.value
          }))
          .sort((a, b) => a.label.props.children.localeCompare(b.label.props.children));

        return {
          id: v.categoryId,
          options: [...selectedValuesSorted, ...unselectedValuesSorted]
        };
      }),
    [options.options, selected?.COUNTRY_CODE]
  );
  useMemo(() => {
    // Clear button enable/disable logic
    const found = typeof selected.COUNTRY_CODE?.length !== 'undefined' && selected.COUNTRY_CODE?.length > 0;
    if (found) {
      setDisableClear(false);
    }
  }, [selected.COUNTRY_CODE]);

  // Clear button click handler
  const clearOnClick = useCallback(() => {
    const clearedValues = [];
    setSelected({
      ...selected,
      COUNTRY_CODE: clearedValues
    });
    setDisableClear(true);
  }, [selected, setSelected]);

  const onUpdate = useCallback(
    (selections: string[]) => {
      if (selections.length === 0) setDisableClear(true);
      setSelected({
        ...selected,
        COUNTRY_CODE: selections
      });
    },
    [selected, setSelected]
  );

  return (
    <div data-cvent-id={`${testID}-countries`} className="my-4 rounded-xl border border-neutral-20 p-2">
      <div className="leading-5 mb-3 ml-1.5 mt-1 flex text-b-md font-normal">{options.labels.title}</div>
      <MultiSelect
        clear={{ label: clear, disable: disableClear, onClick: clearOnClick }}
        labels={{ noMatchFound, title: options.labels.title }}
        locale={options.locale}
        checkBoxGroup={multiSelectOptions}
        onUpdate={onUpdate}
        selected={selected}
      />
    </div>
  );
}
